import { SearchOutlined, Close, KeyboardArrowDown } from '@mui/icons-material';
import { IconButton, Chip, Tooltip, ClickAwayListener } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

import { useProduct } from '../context/ProductContext';
import { useCategory } from '../context/CategoryContext';
import { useDebounce } from '../hooks/useDebounce';
import { Product } from '../models/Product';
import { Category } from '../models/Category';
import { useConfig } from '../utils/useConfig';

function SearchBar() {
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';
  const initialCategoryId = searchParams.get('categoryId') || '';
  const config = useConfig();

  const navigate = useNavigate();
  const { getProducts } = useProduct();
  const { getCategories } = useCategory();
  const searchBarRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuButtonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState<string>(initialSearch);
  const [productList, setProductList] = useState<Product[]>([]);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const debouncedQuery = useDebounce(search, 300);

  // Stan dla wyboru kategorii
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null,
  );
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  // Pobierz kategorie przy pierwszym renderowaniu
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Pobierz tylko kategorie główne (bez podkategorii)
        const { categories: allCategories } = await getCategories();
        const mainCategories = allCategories.filter((c) => !c.parentCategoryId);
        setCategories(mainCategories);

        // Ustaw wybraną kategorię na podstawie URL
        if (initialCategoryId) {
          const selected = mainCategories.find(
            (c) => c.id.toString() === initialCategoryId,
          );
          if (selected) {
            setSelectedCategory(selected);
          }
        }
      } catch (error) {
        console.error('Błąd podczas pobierania kategorii:', error);
      }
    };

    fetchCategories();
  }, [getCategories, initialCategoryId]);

  const getProductsData = useCallback(
    async (query: string) => {
      if (!query.trim()) return;

      try {
        const response = await getProducts({
          draft: false,
          page: 1,
          limit: 5,
          query,
          categoryId: selectedCategory ? selectedCategory.id : null,
        });
        setProductList(response.products);
      } catch (error) {
        console.error('Error fetching search results:', error);
        setProductList([]);
      }
    },
    [getProducts, selectedCategory],
  );

  useEffect(() => {
    if (debouncedQuery !== '') {
      getProductsData(debouncedQuery);
    } else {
      setProductList([]);
    }
  }, [debouncedQuery, getProductsData]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Sprawdzamy czy kliknięcie było poza elementem searchBar i poza przyciskiem menu
      const isOutsideSearchBar =
        searchBarRef.current &&
        !searchBarRef.current.contains(event.target as Node);
      const isOutsideMenuButton =
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target as Node);
      const isOutsideMenu =
        menuRef.current && !menuRef.current.contains(event.target as Node);

      // Jeśli kliknięcie było poza searchBar, zamknij focus
      if (isOutsideSearchBar && isOutsideMenuButton) {
        setIsFocused(false);
      }

      // Jeśli kliknięcie było poza menu i poza przyciskiem menu, zamknij menu
      if (isOutsideMenu && isOutsideMenuButton && menuOpen) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]); // Dodaj menuOpen jako zależność

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!search.trim()) return;

    setIsAnimating(true);
    setTimeout(() => {
      const url = new URL('/szukaj', window.location.origin);
      url.searchParams.set('search', search.trim());
      if (selectedCategory) {
        url.searchParams.set('categoryId', selectedCategory.id.toString());
      }
      navigate(url.pathname + url.search);
      setIsFocused(false);
      setIsAnimating(false);
    }, 300);
  };

  const clearSearch = () => {
    setSearch('');
    setProductList([]);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Obsługa wyboru kategorii
  const handleCategorySelect = (category: Category | null) => {
    setSelectedCategory(category);
    setMenuOpen(false);
    // Resetuj wyniki wyszukiwania po zmianie kategorii
    if (search.trim()) {
      getProductsData(search);
    }
  };

  // Dodaj state do wykrywania rozmiaru ekranu
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth <= 768;

  // Dodaj efekt do nasłuchiwania zmiany rozmiaru okna
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Modyfikujemy funkcję otwierającą/zamykającą menu kategorii
  const toggleCategoryMenu = () => {
    setMenuOpen(!menuOpen);
    // Zamykamy wyniki wyszukiwania, gdy otwieramy menu kategorii
    if (!menuOpen) {
      setIsFocused(false);
    }
  };

  return (
    <div
      ref={searchBarRef}
      className="flex flex-col items-center w-full relative"
    >
      <motion.div
        className="w-full relative"
        initial={{ scale: 0.98, opacity: 0.8 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        <form
          name="search"
          id="search"
          className="flex items-center w-full"
          onSubmit={handleSearch}
        >
          <motion.div
            className={`
              relative flex items-center w-full overflow-hidden z-50
              ${isFocused ? 'ring-2 ring-primary-light' : 'ring-1 ring-gray-200'}
              rounded-md bg-white shadow-sm transition-all duration-300 ease-in-out
            `}
            animate={{
              boxShadow: isFocused
                ? '0 4px 12px rgba(0, 0, 0, 0.08)'
                : '0 1px 3px rgba(0, 0, 0, 0.03)',
            }}
            whileHover={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            }}
          >
            {/* Przycisk wyboru kategorii */}
            <div
              ref={menuButtonRef}
              className={`
                flex-shrink-0 pl-2 pr-1 border-r border-gray-100
                cursor-pointer hidden sm:flex items-center
                hover:bg-gray-50 transition-colors h-full relative
                ${selectedCategory ? 'text-gray-800' : 'text-gray-500'}
              `}
              onClick={toggleCategoryMenu}
            >
              <div className="flex items-center gap-1 py-2 px-1">
                {selectedCategory ? (
                  <Tooltip title="Wybrana kategoria" arrow>
                    <Chip
                      label={selectedCategory.name}
                      size="small"
                      color="primary"
                      variant="outlined"
                      onDelete={(e) => {
                        e.stopPropagation(); // Zapobiega zamknięciu/otwarciu menu
                        handleCategorySelect(null);
                      }}
                      className="max-w-[120px]"
                      sx={{
                        height: '26px',
                        '& .MuiChip-label': {
                          fontSize: '0.75rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        },
                      }}
                    />
                  </Tooltip>
                ) : (
                  <span className="text-sm whitespace-nowrap font-medium">
                    Wszystkie kategorie
                  </span>
                )}
                <KeyboardArrowDown
                  fontSize="small"
                  className={`text-gray-400 transition-transform ${menuOpen ? 'rotate-180' : 'rotate-0'}`}
                />
              </div>
            </div>

            {/* Ikona wyszukiwania */}
            <div className="pl-3 sm:pl-2 flex-shrink-0">
              <motion.div
                animate={isAnimating ? { scale: [1, 1.2, 1] } : {}}
                transition={{ duration: 0.3 }}
              >
                <SearchOutlined
                  className={`${isFocused ? 'text-primary' : 'text-gray-400'} transition-colors duration-200`}
                />
              </motion.div>
            </div>

            {/* Pole wyszukiwania */}
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={() => setIsFocused(true)}
              placeholder={
                selectedCategory
                  ? `Szukaj w kategorii ${selectedCategory.name}...`
                  : 'Szukaj produktów...'
              }
              className="
                w-full py-2.5 px-2 text-gray-700 
                border-none outline-none bg-transparent
                placeholder-gray-400 focus:ring-0
                text-sm
              "
            />

            {/* Przycisk czyszczenia */}
            <AnimatePresence>
              {search && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, x: 5 }}
                  animate={{ opacity: 1, scale: 1, x: 0 }}
                  exit={{ opacity: 0, scale: 0.8, x: 5 }}
                  className="pr-1"
                >
                  <IconButton
                    size="small"
                    onClick={clearSearch}
                    className="text-gray-400 hover:text-gray-600"
                    sx={{ padding: '4px' }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Przycisk wyszukiwania */}
            <motion.button
              type="submit"
              className={`
                flex-shrink-0 z-50 h-full py-2 px-3 sm:px-4
                bg-primary text-white
                flex items-center justify-center gap-1
                transition-all duration-200
                ${!search.trim() ? 'opacity-70 cursor-not-allowed' : 'hover:bg-opacity-90'}
              `}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={!search.trim()}
            >
              <motion.div
                animate={isAnimating ? { rotate: [0, 15, -15, 0] } : {}}
                transition={{ duration: 0.4 }}
              >
                <SearchOutlined fontSize="small" />
              </motion.div>
              <span className={isMobile ? 'hidden sm:inline' : ''}>Szukaj</span>
            </motion.button>
          </motion.div>
        </form>
      </motion.div>

      {/* Menu wyboru kategorii */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="
              w-full absolute top-full left-0 mt-2 z-50
              bg-white rounded-md shadow-lg border border-gray-200
              max-h-[80vh] overflow-hidden
            "
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            ref={menuRef}
          >
            <div className="p-3 border-b border-gray-200 flex justify-between items-center">
              <p className="text-sm text-gray-700 font-medium">
                Wybierz kategorię
              </p>
              <IconButton size="small" onClick={() => setMenuOpen(false)}>
                <Close fontSize="small" />
              </IconButton>
            </div>
            <div className="overflow-y-auto max-h-[60vh] sm:max-h-80 p-1">
              <motion.div
                className={`
                  p-3 hover:bg-gray-50 cursor-pointer rounded-md
                  transition-colors duration-150
                  ${!selectedCategory ? `bg-${config.theme.mainColor}-50` : ''}
                `}
                onClick={() => handleCategorySelect(null)}
                whileHover={{ x: 3 }}
              >
                <p
                  className={`text-sm ${!selectedCategory ? 'font-medium text-primary' : 'text-gray-700'}`}
                >
                  Wszystkie kategorie
                </p>
              </motion.div>

              <div className="border-b border-gray-100 my-1"></div>

              {categories.map((category) => (
                <motion.div
                  key={category.id}
                  className={`
                    p-3 hover:bg-gray-50 cursor-pointer rounded-md
                    transition-colors duration-150
                    ${selectedCategory?.id === category.id ? `bg-${config.theme.mainColor}-50` : ''}
                  `}
                  onClick={() => handleCategorySelect(category)}
                  whileHover={{ x: 3 }}
                >
                  <p
                    className={`text-sm ${selectedCategory?.id === category.id ? 'font-medium text-primary' : 'text-gray-700'}`}
                  >
                    {category.name}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dropdown z wynikami */}
      <AnimatePresence>
        {isFocused && productList.length > 0 && (
          <motion.div
            className="
              w-full absolute top-full left-0 mt-2 z-50
              bg-white rounded-md shadow-lg border border-gray-200
              max-h-[80vh] overflow-hidden
            "
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            <div className="p-2 border-b border-gray-200">
              <p className="text-xs text-gray-600 font-medium px-1 flex items-center gap-1">
                <SearchOutlined fontSize="inherit" />
                Znaleziono {productList.length} wyników
                {selectedCategory && (
                  <>
                    <span className="mx-1">w</span>
                    <Chip
                      label={selectedCategory.name}
                      size="small"
                      color="primary"
                      variant="outlined"
                      className="max-w-[120px]"
                      sx={{
                        height: '20px',
                        '& .MuiChip-label': {
                          fontSize: '0.7rem',
                          padding: '0 6px',
                        },
                      }}
                    />
                  </>
                )}
              </p>
            </div>

            <div className="overflow-y-auto max-h-[60vh] sm:max-h-80">
              {productList.map((product, index) => (
                <motion.div
                  key={product.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                >
                  <motion.a
                    href={`/produkt/${product.slug}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setIsFocused(false);
                      navigate(`/produkt/${product.slug}`);
                    }}
                    className="
                      flex items-center p-3 hover:bg-gray-50
                      border-b border-gray-100 last:border-none
                      transition-colors duration-150
                    "
                    whileHover={{ x: 3 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    <div
                      className="
                      w-12 h-12 rounded-md overflow-hidden 
                      flex-shrink-0 mr-3 bg-white border border-gray-100
                    "
                    >
                      <img
                        src={product.mobilePrimaryImage}
                        alt={product.name}
                        className="w-full h-full object-contain p-1"
                      />
                    </div>
                    <div className="flex-grow min-w-0">
                      <p className="text-sm font-medium text-gray-800 line-clamp-2">
                        {product.name}
                      </p>
                      <div className="flex justify-between items-center mt-1">
                        <p className="text-xs font-bold text-primary">
                          {product.price.toFixed(2)} zł
                        </p>
                        <span className="text-xs text-gray-400 truncate ml-2">
                          {product.serialNumber}
                        </span>
                      </div>
                    </div>
                  </motion.a>
                </motion.div>
              ))}
            </div>

            <div className="p-2 border-t border-gray-200 bg-gray-50">
              <motion.button
                onClick={() => {
                  setIsFocused(false);
                  const url = new URL('/szukaj', window.location.origin);
                  url.searchParams.set('search', search.trim());
                  if (selectedCategory) {
                    url.searchParams.set(
                      'categoryId',
                      selectedCategory.id.toString(),
                    );
                  }
                  navigate(url.pathname + url.search);
                }}
                className="
                  w-full py-2 text-center text-sm font-medium
                  text-primary hover:text-primary-dark flex items-center justify-center
                "
                whileHover={{ x: 3 }}
                transition={{ type: 'spring', stiffness: 400 }}
              >
                Zobacz wszystkie wyniki
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3 w-3 ml-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default SearchBar;
